<template>
  <div
    class="purchase-price-summary"
    :class="{ 'force-mobile': forceMobileView }"
    aria-label="Shopping Cart Summary"
  >
    <v-alert v-if="purchase.refundComment" type="info">
      Refund Comment: {{ purchase.refundComment }}
    </v-alert>

    <v-card variant="outlined" class="purchase-price-summary-card">
      <v-container fluid class="pa-0" role="table" aria-label="Tickets">
        <!-- Tickets Section (single-date) — series purchases use the series-aware block below. -->
        <template
          v-if="
            (purchase?.tickets?.length || !hideEmptyTicketsSection) &&
            !isSeriesPurchase
          "
        >
          <v-row role="row" no-gutters class="section-header">
            <v-col role="columnheader" :cols="hideServiceFee ? 9 : 8">
              <div class="text-h6">Tickets</div>
            </v-col>
            <!--only show price, service fee and subtotal headers on desktop-->
            <v-col
              role="columnheader"
              cols="1"
              class="text-right d-none"
              :class="{ 'd-md-block': !forceMobileView }"
              >Quantity</v-col
            >
            <v-col
              role="columnheader"
              cols="1"
              class="text-right d-none"
              :class="{ 'd-md-block': !forceMobileView }"
              >Price</v-col
            >
            <v-col
              role="columnheader"
              cols="1"
              class="text-right d-none"
              :class="{ 'd-md-block': !forceMobileView }"
              v-if="!hideServiceFee"
              >Service Fee</v-col
            >
            <v-col
              role="columnheader"
              cols="1"
              class="text-right d-none"
              :class="{ 'd-md-block': !forceMobileView }"
              >Total</v-col
            >
          </v-row>

          <template
            v-for="({ firstTicket: ticket, allTickets }, i) in groupedTickets"
            :key="ticket.ticketId"
          >
            <!-- Category Header for Reserved Seating -->
            <v-row
              role="row"
              v-if="i === 0 && ticket.isReservedIndividualSeat"
              no-gutters
              class="category-header"
              aria-label="Reserved Seating Cart Items"
            >
              <v-col role="columnheader" cols="12">
                <h4>RESERVED SEATING</h4>
              </v-col>
            </v-row>

            <!-- Category Header for General Admission -->
            <v-row
              role="row"
              v-if="
                !ticket.isReservedIndividualSeat &&
                (i === 0 ||
                  groupedTickets[i - 1].firstTicket.isReservedIndividualSeat)
              "
              no-gutters
              class="category-header"
              aria-label="General Admission Cart Items"
            >
              <v-col role="columnheader" cols="12">
                <h4>GENERAL ADMISSION</h4>
              </v-col>
            </v-row>
            <!-- Category Item Heading/Buttons -->
            <v-row
              role="row"
              no-gutters
              class="item-row"
              :class="{
                completed: purchase.status == PurchaseStatus.Completed,
              }"
              aria-label="Cart Item Title Heading"
            >
              <v-col
                role="cell"
                cols="12"
                :md="forceMobileView ? '12' : hideServiceFee ? 9 : 8"
                class="item-col"
              >
                <div class="d-flex align-start">
                  <v-btn
                    v-if="
                      isEditable &&
                      ticket.isReservedIndividualSeat &&
                      purchase.status !== PurchaseStatus.Completed
                    "
                    icon
                    :size="resolveSmallComponentSize"
                    variant="text"
                    @click="handleRemoveReservedSeatTicket(ticket)"
                    :aria-label="`Remove ${ticket.description} - Seat ${ticket.seatNumber}`"
                    class="mr-2"
                  >
                    <v-icon color="grey" size="small" aria-hidden="true"
                      >fas fa-trash</v-icon
                    >
                  </v-btn>
                  <div class="flex-grow-1">
                    <div>{{ ticket.description }}</div>
                    <div
                      v-if="purchase?.eventDate?.isSeated"
                      class="item-detail"
                    >
                      <span class="text-grey-darken-3">
                        <span v-if="ticket.isReservedIndividualSeat">Seat:</span
                        ><span v-else>Section:</span>
                      </span>
                      <!-- #3493 - Prefer the resolved human label; raw seat id is fallback. -->
                      {{ ticket.seatLabel ?? ticket.seatNumber }}
                    </div>

                    <!-- for non-seat-map tickets-->
                    <div
                      :class="{ 'd-md-none': !forceMobileView }"
                      class="item-detail"
                      v-if="
                        isEditable &&
                        !ticket.isReservedIndividualSeat &&
                        !purchase.eventDate?.isSeated &&
                        purchase.status !== PurchaseStatus.Completed
                      "
                    >
                      <span class="text-grey-darken-3">Qty:</span>
                      <div class="d-flex align-center">
                        <v-btn
                          v-if="localQuantity[ticket.eventPriceId!] > 0"
                          icon
                          :size="resolveSmallComponentSize"
                          variant="text"
                          @click="handleRemoveUnseatedTicket(ticket)"
                          :aria-label="`Remove all ${localQuantity[ticket.eventPriceId!]} ${ticket.description} tickets`"
                          :disabled="
                            !shouldInputBeEnabled(ticket.eventPriceId || 0)
                          "
                          class="mr-1"
                        >
                          <v-icon color="grey" size="small" aria-hidden="true"
                            >fas fa-trash</v-icon
                          >
                        </v-btn>
                        <quantity-input
                          :value="
                            pendingQuantity[ticket.eventPriceId!] ??
                            localQuantity[ticket.eventPriceId!] ??
                            0
                          "
                          @update="
                            (val) =>
                              setPendingQuantity(ticket.eventPriceId!, val)
                          "
                          :loading="isUpdatingUnseatedGA(ticket.eventPriceId!)"
                          :max="
                            getMaximumPurchaseQuantity(ticket.eventPriceId!)
                          "
                          :zero-on-min="true"
                          :min="
                            eventPriceMap.get(ticket.eventPriceId!)!
                              .minimumQuantity || 0
                          "
                          :disabled="
                            !shouldInputBeEnabled(ticket.eventPriceId!)
                          "
                          :size="resolveSmallComponentSize"
                          :input-aria-label="`Quantity for ${ticket.description}`"
                          minus-aria-label="Remove Seat"
                          plus-aria-label="Add Seat"
                        />
                        <v-btn
                          v-if="hasPendingChange(ticket.eventPriceId!)"
                          variant="outlined"
                          color="primary"
                          :size="resolveSmallComponentSize"
                          :loading="isUpdatingUnseatedGA(ticket.eventPriceId!)"
                          @click="updateUnseatedQuantity(ticket.eventPriceId!)"
                          class="ml-2"
                        >
                          Update
                        </v-btn>
                      </div>
                    </div>
                    <div
                      :class="{ 'd-md-none': !forceMobileView }"
                      class="item-detail"
                      v-if="
                        !isEditable &&
                        !ticket.isReservedIndividualSeat &&
                        !purchase.eventDate?.isSeated
                      "
                    >
                      <span class="text-grey-darken-3">Qty:</span>
                      {{ allTickets.length }}
                    </div>

                    <!-- for seat map tickets-->
                    <div
                      :class="{ 'd-md-none': !forceMobileView }"
                      class="item-detail"
                      v-if="
                        isEditable &&
                        !ticket.isReservedIndividualSeat &&
                        purchase.eventDate?.isSeated &&
                        purchase.status !== PurchaseStatus.Completed
                      "
                    >
                      <div class="d-flex align-center">
                        <quantity-input
                          :value="
                            pendingQuantity[
                              getSeatedKey(
                                ticket.eventPriceId!,
                                ticket.seatNumber!,
                              )
                            ] ??
                            localQuantity[
                              getSeatedKey(
                                ticket.eventPriceId!,
                                ticket.seatNumber!,
                              ) ?? 0
                            ]
                          "
                          @update="
                            (val) =>
                              setPendingSeatedQuantity(
                                ticket.eventPriceId!,
                                ticket.seatNumber!,
                                val,
                              )
                          "
                          :loading="
                            isUpdatingSeatedGA(
                              ticket.eventPriceId!,
                              ticket.seatNumber!,
                            )
                          "
                          :max="
                            getMaximumPurchaseQuantity(ticket.eventPriceId!)
                          "
                          :zero-on-min="true"
                          :min="
                            eventPriceMap.get(ticket.eventPriceId!)!
                              .minimumQuantity || 0
                          "
                          :disabled="
                            !shouldInputBeEnabled(
                              ticket.eventPriceId!,
                              ticket.seatNumber!,
                            )
                          "
                          :size="resolveSmallComponentSize"
                          :input-aria-describedby="`quantity-desc-${ticket.eventPriceId}`"
                          :input-aria-label="`Quantity for ${ticket.description}`"
                          minus-aria-label="Remove Seat"
                          plus-aria-label="Add Seat"
                        />
                        <div>
                          <v-btn
                            v-if="
                              hasPendingSeatedChange(
                                ticket.eventPriceId!,
                                ticket.seatNumber!,
                              )
                            "
                            variant="outlined"
                            color="primary"
                            :size="resolveSmallComponentSize"
                            :loading="
                              isUpdatingSeatedGA(
                                ticket.eventPriceId!,
                                ticket.seatNumber!,
                              )
                            "
                            @click="
                              updateSeatedQuantity(
                                ticket.eventPriceId!,
                                ticket.seatNumber!,
                              )
                            "
                            class="ml-2"
                          >
                            Update
                          </v-btn>
                          <v-btn
                            v-if="
                              hasPendingSeatedChange(
                                ticket.eventPriceId!,
                                ticket.seatNumber!,
                              )
                            "
                            variant="text"
                            color="grey"
                            :size="resolveSmallComponentSize"
                            :disabled="
                              isUpdatingSeatedGA(
                                ticket.eventPriceId!,
                                ticket.seatNumber!,
                              )
                            "
                            @click="
                              clearPendingSeatedQuantity(
                                ticket.eventPriceId!,
                                ticket.seatNumber!,
                              )
                            "
                            class="ml-2 mt-1"
                          >
                            Cancel
                          </v-btn>
                        </div>
                      </div>
                    </div>
                    <template v-if="forceMobileView || isMobile">
                      <div
                        class="item-detail"
                        v-if="
                          !isEditable &&
                          !ticket.isReservedIndividualSeat &&
                          purchase.eventDate?.isSeated
                        "
                      >
                        <span class="text-grey-darken-3">Qty:</span>
                        {{ allTickets.length }}
                      </div>
                      <div
                        :class="{ 'd-md-none': !forceMobileView }"
                        :id="`quantity-desc-${ticket.eventPriceId}`"
                        class="item-detail"
                        v-if="
                          isEditable &&
                          purchase.status !== PurchaseStatus.Completed
                        "
                      >
                        <div
                          class="text-caption pt-1"
                          v-if="
                            eventPriceMap.get(ticket.eventPriceId!)!
                              .minimumQuantity
                          "
                        >
                          <span class="text-grey-darken-3"
                            >Min Purchase Qty:
                            {{
                              eventPriceMap.get(ticket.eventPriceId!)!
                                .minimumQuantity
                            }}</span
                          >
                        </div>
                        <div
                          class="text-caption pt-1"
                          v-if="
                            eventPriceMap.get(ticket.eventPriceId!)!
                              .maximumPurchaseQuantity
                          "
                        >
                          <span class="text-grey-darken-3"
                            >Max Purchase Qty:
                            {{
                              eventPriceMap.get(ticket.eventPriceId!)!
                                .maximumPurchaseQuantity
                            }}</span
                          >
                        </div>
                      </div>
                      <div class="item-detail">
                        <span class="text-grey-darken-3">Ticket Price: </span>
                        <span v-if="!hideServiceFee">
                          {{ formatCurrency(ticket.price) }}
                        </span>
                        <span v-else>
                          {{ formatCurrency(ticket.price + ticket.fees) }}
                        </span>
                      </div>
                      <div class="item-detail" v-if="!hideServiceFee">
                        <span class="text-grey-darken-3">Service Fee: </span>
                        {{ formatCurrency(ticket.fees) }}
                      </div>
                    </template>
                  </div>
                </div>
              </v-col>

              <!-- Desktop: separate columns-->
              <!-- Desktop: Quantity column -->
              <v-col
                role="cell"
                cols="6"
                :md="forceMobileView ? 6 : 1"
                class="text-right d-none"
                :class="{ 'd-md-block': !forceMobileView }"
              >
                <div>
                  {{ allTickets.length }}
                </div>
              </v-col>
              <v-col
                role="cell"
                cols="6"
                :md="forceMobileView ? 6 : 1"
                class="d-none text-right"
                :class="{ 'd-md-block': !forceMobileView }"
              >
                <span v-if="!hideServiceFee">
                  {{ formatCurrency(ticket.price) }}
                </span>
                <span v-else>
                  {{ formatCurrency(ticket.price + ticket.fees) }}
                </span>
              </v-col>
              <v-col
                role="cell"
                cols="6"
                :md="forceMobileView ? 6 : 1"
                class="price-col d-none text-right"
                :class="{ 'd-md-block': !forceMobileView }"
                v-if="!hideServiceFee"
              >
                <span
                  v-if="purchase.waiveTicketFees && ticket.fees == 0"
                  class="font-italic"
                  >Waived</span
                >
                <span v-else>{{ formatCurrency(ticket.fees) }}</span>
              </v-col>

              <!-- Mobile: Price breakdown - only needed if showing service fees-->
              <v-col
                role="cell"
                cols="12"
                :class="{ 'd-md-none': !forceMobileView }"
                class="mobile-price-breakdown"
                v-if="!hideServiceFee"
              >
                <v-row no-gutters>
                  <v-col cols="6" class="text-caption">Ticket Total:</v-col>
                  <v-col cols="6" class="text-right" aria-live="polite">
                    {{
                      formatCurrency(
                        (ticket.fees + ticket.price) * allTickets.length,
                      )
                    }}
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="6" class="text-caption"
                    >Total Service Fees:</v-col
                  >
                  <v-col cols="6" class="text-right" aria-live="polite">
                    <span
                      v-if="purchase.waiveTicketFees && ticket.fees == 0"
                      class="font-italic"
                      >Waived</span
                    >
                    <span v-else>{{
                      formatCurrency(ticket.fees * allTickets.length)
                    }}</span>
                  </v-col>
                </v-row>
              </v-col>

              <v-col
                role="cell"
                cols="12"
                :md="forceMobileView ? 12 : 1"
                class="subtotal-col"
                aria-live="polite"
              >
                <span :class="{ 'd-md-none': !forceMobileView }" class=""
                  >Total:
                </span>
                {{
                  formatCurrency(ticket.subtotalWithFees * allTickets.length)
                }}
              </v-col>
            </v-row>

            <!-- Ticket Details Row -->
            <v-row
              role="row"
              no-gutters
              class="detail-row"
              v-if="
                purchase.status == PurchaseStatus.Completed ||
                isRefundedOrExchanged(ticket)
              "
              aria-label="Cart Item Details"
            >
              <v-col role="cell" cols="12">
                <div class="d-flex align-center justify-space-between">
                  <div class="ticket-id-info">
                    <span class="text-grey-darken-3"
                      >Ticket ID{{ allTickets.length == 1 ? "" : "s" }}:</span
                    >
                    {{
                      ticketIDsToShortString(
                        allTickets.map((t) => t?.ticketId ?? 0),
                      )
                    }}
                  </div>
                  <div class="d-flex align-center">
                    <router-link
                      v-if="ticket.relatedPurchase"
                      class="no-print text-caption"
                      :to="{
                        name: COMMON_ROUTE_NAMES.TicketPurchaseSingle,
                        params: { publicId: ticket.relatedPurchase.publicId },
                      }"
                    >
                      View {{ ticket.relatedPurchase.description }}
                    </router-link>
                    <ticket-status-badge :ticket="ticket" class="ml-2" />
                  </div>
                </div>
              </v-col>
            </v-row>
            <!-- Refund Row -->
            <v-row
              role="row"
              no-gutters
              class="refund-row"
              v-if="isRefundedOrExchanged(ticket)"
            >
              <!-- Refund breakdown 
               -->
              <v-col role="cell" cols="12">
                <v-row role="row" no-gutters>
                  <v-col
                    role="columnheader"
                    cols="11"
                    class="text-caption text-error d-flex justify-end"
                    >Refunded Amount</v-col
                  >
                  <v-col role="cell" cols="1" class="text-right text-error">{{
                    formatCurrency(-ticket.price * allTickets.length)
                  }}</v-col>
                </v-row>
                <v-row role="row" no-gutters v-if="!hideServiceFee">
                  <v-col
                    role="columnheader"
                    cols="11"
                    class="text-caption text-error d-flex justify-end"
                    >Refunded Fees</v-col
                  >
                  <v-col role="cell" cols="1" class="text-right text-error">
                    <span
                      v-if="purchase.waiveTicketFees && ticket.fees == 0"
                      class="font-italic"
                      >Waived</span
                    >
                    <span v-else>{{
                      formatCurrency(-ticket.fees * allTickets.length)
                    }}</span>
                  </v-col>
                </v-row>
                <v-row role="row" no-gutters class="subtotal-col text-error">
                  <v-col
                    role="columnheader"
                    cols="11"
                    class="text-caption text-error d-flex justify-end font-weight-bold"
                    >Refund Total:</v-col
                  >
                  <v-col
                    role="cell"
                    cols="1"
                    class="text-right text-error font-weight-bold"
                    aria-live="polite"
                  >
                    {{
                      formatCurrency(
                        -ticket.subtotalWithFees * allTickets.length,
                      )
                    }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </template>
        </template>

        <!-- Tickets Section (series) — grouped by PassType, one row per ticket-date pairing. -->
        <template v-if="isSeriesPurchase && purchase?.tickets?.length">
          <v-row role="row" no-gutters class="section-header">
            <v-col role="columnheader" :cols="hideServiceFee ? 9 : 8">
              <div class="text-h6">Tickets</div>
            </v-col>
            <v-col
              role="columnheader"
              cols="1"
              class="text-right d-none"
              :class="{ 'd-md-block': !forceMobileView }"
              >Date</v-col
            >
            <v-col
              role="columnheader"
              cols="1"
              class="text-right d-none"
              :class="{ 'd-md-block': !forceMobileView }"
              >Price</v-col
            >
            <v-col
              v-if="!hideServiceFee"
              role="columnheader"
              cols="1"
              class="text-right d-none"
              :class="{ 'd-md-block': !forceMobileView }"
              >Service Fee</v-col
            >
            <v-col
              role="columnheader"
              cols="1"
              class="text-right d-none"
              :class="{ 'd-md-block': !forceMobileView }"
              >Total</v-col
            >
          </v-row>

          <v-row
            v-for="group in seriesTicketGroups"
            :key="group.passTypeId"
            role="row"
            no-gutters
            class="item-row"
          >
            <v-col
              role="cell"
              cols="12"
              :md="forceMobileView ? '12' : hideServiceFee ? 9 : 8"
              class="item-col"
            >
              <div>{{ group.passTypeName }} × {{ group.passQty }}</div>
              <div
                :class="{ 'd-md-none': !forceMobileView }"
                class="text-caption text-medium-emphasis"
              >
                {{ group.dateLabel }}
              </div>
              <!-- Editable pass quantity (cart + public editable summary). Routes through the same
                   service.setSeriesPassTypeQuantity the inline pass selector uses, so the purchase
                   reloads and both surfaces stay in sync; qty 0 removes the pass. -->
              <div
                v-if="
                  isEditable && purchase.status !== PurchaseStatus.Completed
                "
                class="d-flex align-center pt-2"
              >
                <quantity-input
                  :value="seriesLocalQty[group.passTypeId] ?? group.passQty"
                  @update="
                    (val) => handleSeriesQtyUpdate(group.passTypeId, val)
                  "
                  :max="seriesMaxQty(group.passTypeId)"
                  :loading="isUpdatingSeries[group.passTypeId] === true"
                  :size="resolveSmallComponentSize"
                  :aria-label="`Quantity for ${group.passTypeName}`"
                  minus-aria-label="Remove Pass"
                  plus-aria-label="Add Pass"
                />
              </div>
              <template v-if="forceMobileView || isMobile">
                <div class="item-detail">
                  <span class="text-grey-darken-3">Ticket Price: </span>
                  <span v-if="!hideServiceFee">
                    {{ formatCurrency(group.passPrice) }}
                  </span>
                  <span v-else>
                    {{ formatCurrency(group.passPrice + group.passFee) }}
                  </span>
                </div>
                <div class="item-detail" v-if="!hideServiceFee">
                  <span class="text-grey-darken-3">Service Fee: </span>
                  {{ formatCurrency(group.passFee) }}
                </div>
              </template>
            </v-col>
            <v-col
              role="cell"
              cols="6"
              md="1"
              class="text-right d-none"
              :class="{ 'd-md-block': !forceMobileView }"
            >
              {{ group.dateLabel }}
            </v-col>
            <v-col
              role="cell"
              cols="6"
              md="1"
              class="text-right d-none"
              :class="{ 'd-md-block': !forceMobileView }"
            >
              {{ formatCurrency(group.passPrice) }}
            </v-col>
            <v-col
              v-if="!hideServiceFee"
              role="cell"
              cols="6"
              md="1"
              class="text-right d-none"
              :class="{ 'd-md-block': !forceMobileView }"
            >
              {{ formatCurrency(group.passFee) }}
            </v-col>
            <v-col
              role="cell"
              cols="12"
              :class="{ 'd-md-none': !forceMobileView }"
              class="mobile-price-breakdown"
              v-if="!hideServiceFee"
            >
              <v-row no-gutters>
                <v-col cols="6" class="text-caption">Ticket Total:</v-col>
                <v-col cols="6" class="text-right" aria-live="polite">
                  {{ formatCurrency(group.groupTotal) }}
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="6" class="text-caption">Total Service Fees:</v-col>
                <v-col cols="6" class="text-right" aria-live="polite">
                  <span
                    v-if="purchase.waiveTicketFees && group.passFee == 0"
                    class="font-italic"
                    >Waived</span
                  >
                  <span v-else>{{
                    formatCurrency(group.passFee * group.passQty)
                  }}</span>
                </v-col>
              </v-row>
            </v-col>
            <v-col
              role="cell"
              cols="12"
              :md="forceMobileView ? '12' : '1'"
              class="subtotal-col"
              aria-live="polite"
            >
              <span :class="{ 'd-md-none': !forceMobileView }">Total: </span
              >{{ formatCurrency(group.groupTotal) }}
            </v-col>
          </v-row>
          <c-loader-status
            v-if="isEditable"
            :loaders="{ '': [service.setSeriesPassTypeQuantity] }"
            :progress-placeholder="false"
          />
        </template>

        <!-- Discounts Section -->
        <template v-if="purchase?.discounts?.length">
          <v-row role="row" no-gutters class="section-header">
            <v-col role="columnheader" cols="12">
              <div class="text-h6">Discounts</div>
            </v-col>
          </v-row>

          <v-row
            role="row"
            no-gutters
            class="item-row"
            v-for="discount in purchase.discounts.filter(
              (d) => (d?.amount ?? 0) > 0,
            )"
            :key="discount.discountId"
          >
            <v-col role="cell" cols="6" class="item-col">
              {{ discount.description }}
            </v-col>
            <v-col
              role="cell"
              :cols="6"
              class="subtotal-col"
              aria-live="polite"
            >
              <span :class="{ 'd-md-none': !forceMobileView }" class=""
                >Discount:
              </span>
              {{ formatCurrency(-discount.amount) }}
            </v-col>
          </v-row>
        </template>

        <!-- Additional Items Section -->
        <template
          v-if="
            purchase.refundable == RefundableState.Purchased ||
            purchase?.addons?.length ||
            purchase.convenienceFee ||
            purchase.absorbProcessingFee ||
            purchase.salesTax ||
            absorbedTax
          "
        >
          <v-row role="row" no-gutters class="section-header">
            <v-col role="columnheader" cols="12" md="9">
              <div class="text-h6">Additional Items</div>
            </v-col>
            <!-- Desktop column headers -->
            <template v-if="!forceMobileView">
              <v-col
                role="columnheader"
                cols="1"
                class="text-right d-none d-md-block"
                >Quantity</v-col
              >
              <v-col
                role="columnheader"
                cols="1"
                class="text-right d-none d-md-block"
                >Price</v-col
              >
              <v-col
                role="columnheader"
                cols="1"
                class="text-right d-none d-md-block"
                >Total</v-col
              >
            </template>
          </v-row>

          <!-- Purchase Protection -->
          <v-row
            role="row"
            no-gutters
            class="item-row"
            v-if="purchase.refundable == RefundableState.Purchased"
          >
            <v-col
              role="cell"
              cols="11"
              :md="forceMobileView ? 11 : 9"
              class="item-col"
            >
              <div class="d-flex justify-space-between align-center">
                <span>Purchase Protection</span>
              </div>
            </v-col>
            <!-- Desktop columns -->
            <template v-if="!forceMobileView">
              <v-col role="cell" cols="1" class="text-right d-none d-md-block"
                >1</v-col
              >
              <v-col
                role="cell"
                cols="1"
                class="text-right d-none d-md-block"
                aria-live="polite"
              >
                {{ formatCurrency(purchase.refundableFee) }}
              </v-col>
            </template>
            <!-- This will show in desktop or mobile -->
            <v-col
              role="cell"
              cols="1"
              class="subtotal-col text-right d-md-block"
              aria-live="polite"
            >
              {{ formatCurrency(purchase.refundableFee) }}
            </v-col>
          </v-row>

          <!-- Purchase Protection Refund -->
          <!-- this would only show up for the agent so mobile support not needed-->
          <v-row
            role="row"
            no-gutters
            class="refund-row"
            v-if="purchase.refundedRefundableFee"
          >
            <v-col role="columnheader" cols="10" class="item-col">
              Purchase Protection (refund)
            </v-col>
            <v-col
              role="cell"
              cols="2"
              class="subtotal-col text-error text-right"
              aria-live="polite"
            >
              {{ formatCurrency(purchase.refundableFee) }}
            </v-col>
          </v-row>

          <!-- Addons -->
          <template
            v-for="{ firstAddon: addon, allAddons } in groupedAddons"
            :key="addon.addonId"
          >
            <v-row role="row" no-gutters class="item-row">
              <v-col
                role="cell"
                cols="12"
                :md="forceMobileView ? 12 : 9"
                class="item-col"
              >
                <div class="d-flex align-center justify-space-between">
                  <span>{{ addon.name }}</span>
                  <span
                    v-if="forceMobileView || isMobile"
                    class="font-weight-bold"
                  >
                    {{ formatCurrency(addon.price * allAddons.length) }}
                  </span>
                </div>
                <div
                  v-if="forceMobileView || isMobile"
                  class="text-caption text-grey-darken-3"
                >
                  Qty: {{ allAddons.length }} &nbsp; | &nbsp; Price:
                  {{ formatCurrency(addon.price) }}
                </div>
              </v-col>
              <!-- Desktop columns -->
              <template v-if="!forceMobileView">
                <v-col
                  role="cell"
                  cols="1"
                  class="text-right d-none d-md-block"
                >
                  {{ allAddons.length }}
                </v-col>
                <v-col
                  role="cell"
                  cols="1"
                  class="text-right d-none d-md-block"
                >
                  {{ formatCurrency(addon.price) }}
                </v-col>
                <v-col
                  role="cell"
                  cols="1"
                  class="subtotal-col text-right d-none d-md-block"
                  aria-live="polite"
                >
                  {{ formatCurrency(addon.price * allAddons.length) }}
                </v-col>
              </template>
            </v-row>

            <!-- Addon Details Row  -->
            <v-row
              role="row"
              no-gutters
              class="detail-row"
              v-if="purchase.status == PurchaseStatus.Completed"
            >
              <v-col role="cell" cols="12" :md="forceMobileView ? 12 : 9">
                <div class="d-flex align-center justify-space-between">
                  <div class="ticket-id-info">
                    <span class="text-grey-darken-3"
                      >Item ID{{ allAddons.length == 1 ? "" : "s" }}:</span
                    >
                    {{
                      ticketIDsToShortString(
                        allAddons.map((t) => t?.addonId ?? 0),
                        "A",
                      )
                    }}
                  </div>
                  <div class="d-flex align-center">
                    <router-link
                      v-if="addon.relatedPurchase"
                      class="no-print text-caption"
                      :to="{
                        name: COMMON_ROUTE_NAMES.TicketPurchaseSingle,
                        params: { publicId: addon.relatedPurchase.publicId },
                      }"
                    >
                      View {{ addon.relatedPurchase.description }}
                    </router-link>
                    <addon-status-badge :addon="addon" class="ml-2" />
                  </div>
                </div>
              </v-col>
            </v-row>

            <!-- Addon Refund Row -->
            <!-- this would only show up for agent, so mobile support not needed-->
            <v-row
              role="row"
              no-gutters
              class="refund-row"
              v-if="addon.refundedPrice"
            >
              <v-col role="cell" cols="12" md="9" class="item-col"></v-col>
              <template>
                <v-col
                  role="cell"
                  cols="1"
                  class="text-right d-none d-md-block"
                >
                  {{ allAddons.length }}
                </v-col>
                <v-col
                  role="cell"
                  cols="1"
                  class="text-right d-none d-md-block"
                >
                  {{ formatCurrency(-addon.refundedPrice) }}
                </v-col>
                <v-col
                  role="cell"
                  cols="1"
                  class="subtotal-col text-error text-right d-none d-md-block"
                  aria-live="polite"
                >
                  {{ formatCurrency(-addon.refundedPrice * allAddons.length) }}
                </v-col>
              </template>
            </v-row>
          </template>

          <!-- Convenience Fee -->
          <v-row
            role="row"
            no-gutters
            class="item-row"
            v-if="
              notCashOnlyPayment() &&
              (purchase.convenienceFee || purchase.absorbProcessingFee)
            "
          >
            <v-col role="columnheader" cols="10" class="item-col">
              Convenience Fee
            </v-col>
            <v-col
              role="cell"
              cols="2"
              class="subtotal-col text-right"
              aria-live="polite"
            >
              <span v-if="purchase.absorbProcessingFee" class="font-italic"
                >Waived</span
              >
              <span v-else>{{ formatCurrency(purchase.convenienceFee) }}</span>
            </v-col>
          </v-row>

          <!-- Convenience Fee Refund Row -->
          <v-row
            role="row"
            no-gutters
            class="refund-row"
            v-if="purchase.refundedConvenienceFee"
          >
            <v-col role="columnheader" cols="10" class="item-col">
              Convenience Fee (refund)
            </v-col>
            <v-col
              role="cell"
              cols="2"
              class="subtotal-col text-error text-right"
              aria-live="polite"
            >
              {{ formatCurrency(purchase.refundedConvenienceFee) }}
            </v-col>
          </v-row>

          <!-- Sales Tax -->
          <v-row
            role="row"
            no-gutters
            class="item-row"
            v-if="purchase.salesTax || absorbedTax"
          >
            <v-col role="columnheader" cols="10" class="item-col">
              Sales Tax
            </v-col>
            <v-col
              role="cell"
              cols="2"
              class="subtotal-col text-right"
              aria-live="polite"
            >
              <span v-if="absorbedTax" class="font-italic">Waived</span>
              <span v-else>{{ formatCurrency(purchase.salesTax) }}</span>
            </v-col>
          </v-row>

          <!-- Sales Tax Refund Row -->
          <v-row
            role="row"
            no-gutters
            class="refund-row"
            v-if="purchase.refundedSalesTax"
          >
            <v-col role="columnheader" cols="10" class="item-col">
              Sales Tax (refund)
            </v-col>
            <v-col
              role="cell"
              cols="2"
              class="subtotal-col text-error text-right"
              aria-live="polite"
            >
              {{ formatCurrency(purchase.refundedSalesTax) }}
            </v-col>
          </v-row>
        </template>

        <!-- Grand Total -->
        <v-row
          role="row"
          no-gutters
          class="total-row"
          v-if="purchase.grandTotal || !hideEmptyTicketsSection"
        >
          <v-col
            role="cell"
            cols="12"
            :aria-label="`Grand Total: ${formatCurrency(purchase.grandTotal)}`"
            aria-live="polite"
          >
            <div class="text-h6 text-right" aria-hidden="true">
              <span class="pr-2">Grand Total: </span>
              {{ formatCurrency(purchase.grandTotal) }}
            </div>
          </v-col>
        </v-row>

        <!-- Payments Section -->
        <template
          v-if="purchase?.payments?.length || !hideEmptyPaymentsSection"
        >
          <v-row role="row" no-gutters class="section-header">
            <v-col role="columnheader" cols="12">
              <div class="text-h6">Payments</div>
            </v-col>
          </v-row>

          <v-row
            role="row"
            no-gutters
            class="item-row"
            v-for="payment in purchase.payments"
            :key="payment.paymentId"
          >
            <v-col
              role="cell"
              cols="12"
              :md="hideServiceFee ? 6 : 10"
              class="item-col"
            >
              {{ payment.description }}
              <span
                v-if="payment?.relatedPurchases?.length"
                class="float-right"
              >
                <router-link
                  v-for="related in payment.relatedPurchases"
                  :key="related.publicId"
                  class="no-print d-block"
                  :to="{
                    name: COMMON_ROUTE_NAMES.TicketPurchaseSingle,
                    params: { publicId: related.publicId },
                  }"
                >
                  {{ related.description }}
                </router-link>
              </span>
            </v-col>
            <v-col
              role="cell"
              :cols="12"
              :md="hideServiceFee ? 6 : 2"
              class="subtotal-col"
              aria-live="polite"
            >
              {{ formatCurrency(payment.amount) }}
            </v-col>
          </v-row>

          <!-- Balance -->
          <v-row role="row" no-gutters class="total-row">
            <v-col role="cell" cols="12">
              <div class="text-h6 text-right">
                <span class="pr-2">Balance: </span>
                <span
                  :class="purchase.balance < 0 ? 'error--text' : ''"
                  aria-live="polite"
                >
                  {{ formatCurrency(purchase.balance) }}
                </span>
              </div>
            </v-col>
          </v-row>
        </template>
      </v-container>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import {
  TicketStatus,
  TicketPurchaseTicket,
  PurchaseType,
  PurchaseStatus,
  RefundableState,
  EventPriceDto,
  PaymentKind,
} from "@/models.g";
import { TicketPurchaseDtoViewModel } from "@/viewmodels.g";
import {
  formatCurrency,
  getTicketGroupings,
  getAddonGroupings,
} from "@common/utils";
import { buildSeriesTicketGroups } from "@common/purchase/series-cart";
import { maxQuantity as maxQuantityOf } from "@common/purchase/pass-quantity-selector-logic";
import { COMMON_ROUTE_NAMES } from "@common/routing";
import { useSeatingTicketChange } from "@common/composables/useSeatingTicketChange";
import { PublicPurchaseServiceViewModel } from "@/viewmodels.g";
import QuantityInput from "@common/components/quantity-input.vue";
import { computed, toRef, ref, watch } from "vue";
import TicketStatusBadge from "./ticket-status-badge.vue";
import AddonStatusBadge from "./addon-status-badge.vue";
import { useUnseatedTicketChange } from "@common/composables/useUnseatedTicketChange";
import { useDisplay } from "vuetify";
import { useTicketChange } from "@common/composables/useTicketChange";
import useAccessibility from "@common/composables/useAccessibility";

const props = withDefaults(
  defineProps<{
    purchase: TicketPurchaseDtoViewModel;
    hideEmptyPaymentsSection?: boolean;
    forceMobileView?: boolean; //this is for the shopping cart - it present the mobile view even if on desktop
    //the public facing view uses this component in an editable state
    isEditable?: boolean;
    service?: PublicPurchaseServiceViewModel;
  }>(),
  {
    hideEmptyPaymentsSection: false,
    isEditable: false,
    forceMobileView: false,
  },
);
const display = useDisplay();
const { resolveSmallComponentSize } = useAccessibility();
const isMobile = computed(() => display.smAndDown.value);

const emit = defineEmits<{
  "remove-reserved-seat-ticket": [ticket: TicketPurchaseTicket];
}>();

// Use provided service or create new one
const service = props.service || new PublicPurchaseServiceViewModel();

// memoized lookup for event prices
const eventPriceMap = computed(() => {
  const map = new Map<number, EventPriceDto>();
  props.purchase.eventDate?.eventPrices?.forEach((ep) => {
    map.set(ep.id!, ep);
  });
  return map;
});

const localQuantity = ref<Record<string, number>>({});
const pendingQuantity = ref<Record<string, number>>({});
function refreshLocalQuantity() {
  const map: Record<string, number> = {};
  props.purchase.tickets?.forEach((t) => {
    if (t.seatNumber) {
      // Reserved GA/seat map ticket: use eventPriceId-seatNumber as key
      map[`${t.eventPriceId!}-${t.seatNumber}`] =
        (map[`${t.eventPriceId!}-${t.seatNumber}`] || 0) + 1;
    } else {
      // General Admission: use eventPriceId as key, accumulate count
      map[t.eventPriceId!.toString()] =
        (map[t.eventPriceId!.toString()] || 0) + 1;
    }
  });
  localQuantity.value = map;
  // Clear pending changes when local quantity is refreshed
  pendingQuantity.value = {};
}

//watch for if the user changes it on the main page outside of the shopping cart...
watch(
  () => props.purchase.tickets,
  () => {
    refreshLocalQuantity();
  },
  { immediate: true },
);

const {
  isUpdatingUnseatedGA,
  setupUnseatedTicketChange,
  handleUnseatedGAQuantityUpdate,
} = useUnseatedTicketChange(props.purchase, service, () => {});

const { getMaximumPurchaseQuantity } = useTicketChange(props.purchase);

const {
  handleSeatedGAQuantityUpdate,
  isUpdatingSeatedGA,
  setupSeatedTicketChange,
} = useSeatingTicketChange(
  toRef(() => props.purchase),
  service,
);

const anyPendingChange = computed(() => {
  const ret = Object.keys(pendingQuantity.value).some((key) => {
    const pending = pendingQuantity.value[key];

    // For seat map, key may be "eventPriceId-seatNumber"
    const current = localQuantity.value[key] || 0;
    return pending !== undefined && pending !== current;
  });
  return ret;
});

async function handleRemoveUnseatedTicket(ticket: TicketPurchaseTicket) {
  setPendingQuantity(ticket.eventPriceId!, 0);
  await updateUnseatedQuantity(ticket.eventPriceId!);
}

function shouldInputBeEnabled(
  eventPriceId: number,
  seatNumber?: string,
): boolean {
  if (anyPendingChange.value) {
    // Only enable the row with the pending change
    return seatNumber
      ? hasPendingSeatedChange(eventPriceId, seatNumber)
      : hasPendingChange(eventPriceId);
  }
  // No pending changes, all inputs enabled
  return true;
}
function handleRemoveReservedSeatTicket(ticket: TicketPurchaseTicket) {
  if (!ticket.ticketId || !ticket.isReservedIndividualSeat) return;
  emit("remove-reserved-seat-ticket", ticket);
}
const hideServiceFee = computed(() => {
  return props.purchase.isPublic && props.purchase.hideServiceFee;
});

const hideEmptyTicketsSection = computed(() => {
  return props.purchase.type == PurchaseType.Refund;
});

const absorbedTax = computed(() => {
  return props.purchase.absorbedTax;
});

const groupedTickets = computed(() => {
  return getTicketGroupings(props.purchase.tickets!, props.purchase.eventDate!);
});

const isSeriesPurchase = computed(() => props.purchase.eventSeriesId != null);

const seriesTicketGroups = computed(() =>
  isSeriesPurchase.value
    ? buildSeriesTicketGroups(
        props.purchase.tickets ?? [],
        props.purchase.eventSeries?.passTypes ?? [],
      )
    : [],
);

// Editable series cart rows. State is keyed by passTypeId because the summary renders every pass
// group at once (unlike the inline selector, which owns one passType per row component). The update
// path mirrors purchase-pass-quantity-selector-row.vue: optimistic local value, loading flag, and a
// roll-back + message on a failed ItemResult. Concurrency/reload are already configured on the
// shared service by the inline selector, so we don't reconfigure them here.
const seriesLocalQty = ref<Record<number, number>>({});
const isUpdatingSeries = ref<Record<number, boolean>>({});

// Keep the cart inputs in step with edits made from the inline pass selector (both read the same
// purchase.tickets). Reuses the existing tickets watch cadence via its own watch to avoid coupling.
watch(
  seriesTicketGroups,
  (groups) => {
    const map: Record<number, number> = {};
    groups.forEach((g) => (map[g.passTypeId] = g.passQty));
    seriesLocalQty.value = map;
  },
  { immediate: true },
);

function seriesPassTypeById(id: number) {
  return props.purchase.eventSeries?.passTypes?.find((p) => p.id === id);
}

function seriesMaxQty(id: number): number | undefined {
  const pt = seriesPassTypeById(id);
  return pt ? maxQuantityOf(pt) : undefined;
}

async function handleSeriesQtyUpdate(passTypeId: number, val: number) {
  const current =
    seriesTicketGroups.value.find((g) => g.passTypeId === passTypeId)
      ?.passQty ?? 0;
  if (val === current || isUpdatingSeries.value[passTypeId]) return;
  seriesLocalQty.value[passTypeId] = val;
  isUpdatingSeries.value[passTypeId] = true;
  const caller = service.setSeriesPassTypeQuantity;
  try {
    await caller(props.purchase.publicId, passTypeId, val);
  } catch {
    // A failed ItemResult (capacity, max-per-order, sold out) rejects as an HTTP error but still
    // populates the caller state. Swallow the rejection so it isn't unhandled; the failure is
    // surfaced to the user by the <c-loader-status> bound to this caller in the template.
  }
  // Roll the input back to the charged quantity when the caller reports failure.
  if (caller.wasSuccessful === false) {
    seriesLocalQty.value[passTypeId] = current;
  }
  isUpdatingSeries.value[passTypeId] = false;
}

const groupedAddons = computed(() => {
  return getAddonGroupings(props.purchase.addons!);
});

function isRefundedOrExchanged(ticket: TicketPurchaseTicket) {
  return (
    ticket.status == TicketStatus.Refunded ||
    ticket.status == TicketStatus.Exchanged
  );
}

function ticketIDsToShortString(ids: number[], prefix: string = "") {
  const sorted = [...ids].sort();
  let ret = "";

  for (let i = 0; i < sorted.length; i++) {
    let currentId = sorted[i];

    ret += ", " + prefix + currentId;

    let rangeLength = 0;
    for (;;) {
      const nextId = sorted[i + 1];
      if (nextId && nextId == currentId + 1) {
        i++;
        currentId = nextId;
        rangeLength++;
      } else {
        break;
      }
    }
    if (rangeLength > 1) {
      ret += "–" + prefix + currentId;
    } else if (rangeLength == 1) {
      ret += ", " + prefix + currentId;
    }
  }

  return ret.replace(/^,\s+/, "");
}

function setPendingQuantity(eventPriceId: number, value: number) {
  const key = eventPriceId.toString();
  pendingQuantity.value[key] = value;
}

function setPendingSeatedQuantity(
  eventPriceId: number,
  seatNumber: string,
  value: number,
) {
  const key = getSeatedKey(eventPriceId, seatNumber);
  pendingQuantity.value[key] = value;
}

function getSeatedKey(eventPriceId: number, seatNumber: string): string {
  return `${eventPriceId}-${seatNumber}`;
}

function hasPendingChange(eventPriceId: number): boolean {
  const key = eventPriceId.toString();
  const pending = pendingQuantity.value[key];
  const current = localQuantity.value[eventPriceId] || 0;
  return pending !== undefined && pending !== current;
}

function hasPendingSeatedChange(
  eventPriceId: number,
  seatNumber: string,
): boolean {
  const key = getSeatedKey(eventPriceId, seatNumber);
  const pending = pendingQuantity.value[key];
  const current = localQuantity.value[key] || 0;
  return pending !== undefined && pending !== current;
}

function clearPendingSeatedQuantity(eventPriceId: number, seatNumber: string) {
  const key = getSeatedKey(eventPriceId, seatNumber);
  delete pendingQuantity.value[key];
}

async function updateUnseatedQuantity(eventPriceId: number) {
  const key = eventPriceId.toString();
  const pendingValue = pendingQuantity.value[key];

  if (pendingValue !== undefined) {
    await handleUnseatedGAQuantityUpdate(
      eventPriceId,
      pendingValue,
      localQuantity.value[key],
    );
    // Clear pending change after successful update
    delete pendingQuantity.value[key];
  }
}

async function updateSeatedQuantity(eventPriceId: number, seatNumber: string) {
  const key = getSeatedKey(eventPriceId, seatNumber);
  const pendingValue = pendingQuantity.value[key];

  if (pendingValue !== undefined) {
    await handleSeatedGAQuantityUpdate(
      eventPriceId,
      pendingValue,
      localQuantity.value[key],
      seatNumber,
      refreshLocalQuantity,
    );
    // Clear pending change after successful update
    delete pendingQuantity.value[key];
  }
}
setupUnseatedTicketChange();
setupSeatedTicketChange();

const notCashOnlyPayment = () => {
  const payments = props.purchase.payments;

  if (!payments || payments.length == 0) {
    return true;
  }

  return payments.find((p) => p.paymentKind === PaymentKind.CreditCardPayment);
};
</script>

<style lang="scss">
//remove the below once
.purchase-price-summary {
  td {
    padding: 4px 16px !important;
  }
}
.purchase-price-summary-card {
  .category-header {
    padding: 8px 16px 4px;
    font-size: 0.75rem;
    font-weight: 700;
    color: #495057;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background-color: #f8f8f8;
    border-bottom: 1px solid #e0e0e0;
  }
  .header-row {
    background-color: #f5f5f5;
    padding: 12px 16px;
    font-weight: 600;
    border-bottom: 2px solid #e0e0e0;
  }

  .section-header {
    background-color: #fafafa;
    padding: 8px 16px;
    font-weight: 600;
    border-top: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
  }

  .item-row {
    padding: 8px 16px;
    border-bottom: 1px solid #e0e0e0;
    align-items: center;

    &.completed {
      border-bottom: none;
    }

    .item-col {
      .item-detail {
        font-size: 0.75rem;
        color: #495057;
        margin-top: 2px;
      }
    }

    .price-col,
    .subtotal-col {
      text-align: right;
      font-weight: 500;
    }

    .subtotal-col {
      text-align: right;
      font-weight: 600;
    }
  }

  .detail-row {
    background-color: #fafafa;
    padding: 8px 16px;
    border-bottom: 1px solid #e0e0e0;

    .ticket-id-info {
      font-size: 0.75rem;
      color: #495057;
    }
  }

  .total-row {
    padding: 6px 16px;
  }
  .refund-row {
    background-color: #ffebee;
    padding: 8px 16px;
    border-bottom: 1px solid #e0e0e0;

    .text-error {
      color: #d32f2f !important;
    }

    .mobile-refund-breakdown {
      font-size: 0.75rem;
      padding: 4px 0;
    }
  }

  @mixin mobile-styles {
    .section-header {
      background: none;
      padding: 16px 8px 8px;
      border: none;
      border-bottom: 1px solid #e0e0e0;
    }

    .item-row {
      .item-col {
        margin-bottom: 4px;
      }

      .mobile-price-breakdown {
        padding: 4px 0;
        margin-bottom: 4px;
      }

      .subtotal-col {
        text-align: right;
        padding-top: 4px;
      }
    }

    .detail-row {
      margin: -8px 8px 8px;
      padding: 8px;
      border-radius: 0 0 4px 4px;
    }
  }
  // Mobile styles
  @media (max-width: 960px) {
    @include mobile-styles;

    .item-row {
      .item-col {
        .item-detail {
          font-size: inherit !important;
        }
      }
    }

    .text-caption {
      font-size: inherit !important;
    }
  }

  .purchase-price-summary.force-mobile {
    .purchase-price-summary-card {
      @include mobile-styles;
    }
  }
}
</style>
